// TODO: Temporary utils until provisioning page is completed

import _ from 'lodash'
import jwt from 'jsonwebtoken'
import { getStoreInfo, isConfigured } from '@atredux/selectors/storeSelector'
import { store } from '@atredux/store'
import {
    setCurrentCountryCode,
    setCurrentDomainAndRegion,
    VALID_DOMAINS,
    VALID_REGIONS,
} from '@atredux/slices/storeSlice'
import { IDevice } from '@atredux/slices/deviceSlice'
import { getCurrentDevice } from '@atredux/selectors/deviceSelector'
import { defaultLocaleByCountryCode, Locale, setLocale } from '@atredux/slices/localeSlice'
import { getStoredLocale } from '@src/i18n/localeSession'
import { DEFAULT_LOCALE, resolveLocale } from '@amzn/jwo-store-management-platform-components'
import { generateDeferredPromise } from '@utils/DeferredPomiseUtil'
import { _getParameterByName, getDomain, getRegion, getStoreId } from '@utils/EnvironmentUtil'
import { setDeviceInfo } from '@services/DeviceService'
import { setCurrentStore } from '@services/StoreService'
import { UserTypeService } from '@pages/UserTypeSelection/utils/UserTypeService'
import { IhmLoginTokenProvider } from '@api/utils/IhmLoginTokenProvider'
import { UserTypeSelectionRequiredException } from '@utils/Exceptions/BaseToolCommonExceptions'
import { StoreIdService } from '@pages/StoreIdSelection/utils/StoreIdService'
import { EnvironmentDetail, DeviceDetails } from '@amzn/ihm-associate-tools-js-lib'
import * as ATRoleService from '@services/AssociateToolsRoleService'
import CloudFrontSignerCookieRenewalService from '@services/CloudFrontSignerCookieRenewalService'
import { AssociateToolsLoginAuditTrailService } from '@services/AssociateToolsLoginAuditTrailService'
import { IIBTokenProvider } from '@api/utils/IIBTokenProvider'
import { TokenUtils } from '@utils/TokenUtils'
import { cordovaExec } from '@services/CandyCaneService'
import { EnvironmentPluginConstants } from '@utils/Constants/EnvironmentPluginConstants'
import { IUserData } from '@utils/UserData'
import { timeZoneToCountryMap } from '@utils/TimeZoneToCountryUtil'

export const validateDomainAndRegion = (): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
        const domain = getDomain()
        console.log(`Validating domain \"${domain}\" against the valid domains`, VALID_DOMAINS)
        if (!domain || domain.trim() === '') {
            reject(new Error('A domain is required.'))
            return
        }

        let region = getRegion()
        console.log(`Validating region \"${region}\" against the valid regions`, VALID_REGIONS)
        if (!region || region.trim() === '') {
            console.warn(`Missing region: ${region} found, defaulting to PDX...`)
            region = VALID_REGIONS.PDX.toString()
        }

        store.dispatch(setCurrentDomainAndRegion({ domain, region }))
        resolve()
    })
}

/**
 * Handles countryCode, locale, and device setup after store info has been set.
 */
const handlePostStoreInfoSetup = (storeId: string): Promise<Partial<IDevice>> => {
    const countryCode = determineCountryCodeFromTimeZoneOrFallback(storeId)
    store.dispatch(setCurrentCountryCode(countryCode))

    const locale: Locale = determineLocale(countryCode)
    store.dispatch(setLocale(locale))

    return setDeviceInfo(storeId)
}

/**
 * Determines the country code using the timeZone field from Libby (via ATCore).
 * Falls back to storeId-based parsing logic for backward compatibility.
 */
const determineCountryCodeFromTimeZoneOrFallback = (storeId: string): string => {
    const storeInfo = getStoreInfo(store.getState())
    const timeZone = storeInfo?.timeZone
    if (timeZone && timeZoneToCountryMap[timeZone]) {
        console.log(`Resolved countryCode from timeZone '${timeZone}': ${timeZoneToCountryMap[timeZone]}`)
        return timeZoneToCountryMap[timeZone]
    }

    console.warn(`TimeZone missing or unrecognized ('${timeZone}'), falling back to storeId-based logic`)

    // <Country>-<State/Province>-<Number>, e.g. US-WA-3, GB-ENG-123
    // <Continent>-AA-<Number>, e.g. NA-AA-121, EU-AA-97
    const storeIdPieces = storeId.match(/^(\w+)\-\w+\-\d+/) || []
    const country = storeIdPieces[1]
    switch (country) {
    case 'NA':
        return 'US'
    case 'EU':
        return 'GB'
    case 'AP':
        return 'US'
    default:
        return country ?? 'US'
    }
}

/**
 * Determines the locale to use given the current region
 * and store.
 *
 * If a `locale` URL parameter is present, it takes precedence
 * over any store or region level mapping.
 *
 * The locale is mapped to the country specific default for provided countryCode
 */
function determineLocale (countryCode: string): Locale {

    const locale = _getParameterByName('locale')
    // Return locale from query param if set. Resolved rather than looked up
    // directly, so a regional variant the native shell might send (es-MX) maps to
    // the language we ship (es-ES) instead of becoming undefined.
    if (_.isString(locale) && locale.trim() !== '') {
        return resolveLocale(locale)
    }

    // An explicit choice from the language switcher outranks the store's country
    // default, otherwise provisioning would silently revert the user's language
    // on every reload — they picked ja-JP, but a GB store would force en-GB back.
    const storedLocale = getStoredLocale()
    if (storedLocale) {
        console.log('Using stored locale preference: ' + storedLocale)
        return resolveLocale(storedLocale)
    }

    if (defaultLocaleByCountryCode[countryCode]) {
        console.log('Found the locale for the store: ' + defaultLocaleByCountryCode[countryCode])
    } else {
        console.log('Locale not found, set to ' + DEFAULT_LOCALE + ' by default.')
    }

    return defaultLocaleByCountryCode[countryCode] || Locale[DEFAULT_LOCALE]
}

export const emitAssociateToolsLoginAuditEvent = async () => {
    const storeId = StoreIdService.getSelectedStoreId() || EnvironmentDetail.getStoreId()
    const devicePlatform = EnvironmentDetail.getDevicePlatform()

    console.info('ATBase: AssociateToolsLogin: store=' + storeId + ', devicePlatform=' + devicePlatform)

    // currently, users are not concerned with browser logins, only with physical device logins to help with device tracking
    if (!EnvironmentDetail.isBrowser()) {
        try {
            const deviceDetails: {deviceSerialNumber?: string} = await DeviceDetails.getDeviceDetails()
            const dsn = deviceDetails.deviceSerialNumber
            console.info('ATBase: AssociateToolsLogin: dsn=' + dsn)
            return AssociateToolsLoginAuditTrailService.emitAssociateToolsLoginEvent(storeId, devicePlatform, dsn)
        } catch (exception) {
            console.error('ATBase: AssociateToolsLogin: encountered an exception', exception)
            return Promise.reject(exception)
        }
    } else {
        return Promise.resolve()
    }
}

export const setIsWfmFlag = async () => {
    try {
        if (EnvironmentDetail.isIIBAuthN()) {
            const idToken = await IIBTokenProvider.getIdToken()
            const idTokenDecoded = jwt.decode(idToken)
            if (idTokenDecoded['cognito:groups'][0] === 'wfm') {
                UserTypeService.setIsWfmLoginTrue()
            }
        }
        return Promise.resolve()
    } catch (exception) {
        console.error('ATBase: error setting WFM flag', exception)
        return Promise.reject(exception)
    }
}

export const setUserDataToSharedPref = async (logoutTime: number = 0) => {
    try {
        console.log('ATBase: Storing userData to shared pref')
        const IIB_LAST_LOGGEDIN_USER_ID: string = 'IIBLastLoggedInUser'

        const userId = await TokenUtils.getUserAliasFromIIBToken()
        if (!userId) {
            console.warn('ATBase: getUserAliasFromIIBToken returned null or undefined')
            return Promise.reject(new Error('SMP: setUserDataToSharedPref: userId not present (getUserAliasFromIIBToken returned null or undefined)'))
        }

        const userData: IUserData = { userId, logoutTime }
        console.log('ATBase: username to store in shared pref: ', userData)

        const isCordovaAvailable = (window as any).cordova && _.isFunction((window as any).cordova.exec)
        if (!isCordovaAvailable) {
            console.log('ATBase: Cordova is not available')
            return Promise.reject(new Error('SMP: setUserDataToSharedPref: Cordova is not available'))
        }
        console.log('ATBase: Executing cordova call to set the userData in shared preferences')
        await cordovaExec(EnvironmentPluginConstants.ENVIRONMENT_PLUGIN_NAME,
            EnvironmentPluginConstants.ACTION_SET_SHARED_PREFERENCES_UNENCRYPTED,
            [constructJson(IIB_LAST_LOGGEDIN_USER_ID, userData)])
        return Promise.resolve()
    } catch (error) {
        console.error('ATBase: Error storing user data:', error)
        return Promise.reject(error instanceof Error ? error : new Error('SMP: setUserDataToSharedPref: failed to store user data'))
    }
}

export const constructJson = <T>(jsonKey: string, jsonValue: T): Record<string, T> => {
    const jsonObj: Record<string, T> = {}
    jsonObj[jsonKey] = jsonValue
    return jsonObj
}
